/**
 * CompanyModules.jsx — Moduloversikt for bedriften
 *
 * To moduser:
 *   1. Info (default) — klikk åpner modul eller teaser
 *   2. Marketplace — «Be om tilgang»-knapper for inaktive moduler
 *
 * «Legg til moduler»-knappen er kun synlig for admin.
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import {
  Briefcase, Home, GraduationCap, Users, CheckCircle, Lock,
  ChevronRight, Search, Sparkles, Shield, FileSignature,
  Building2, ShoppingCart, X, Send, Loader2, ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ═══════════════════════════════════════════════════════════════
// MODUL-KATEGORIER (alle B2B-moduler)
// ═══════════════════════════════════════════════════════════════
const MODULE_CATEGORIES = [
  {
    id: 'hr',
    title: 'HR & Arbeidsliv',
    icon: Briefcase,
    color: '#B87333',
    modules: [
      {
        key: 'arbeidsrett-bedrift',
        name: 'Arbeidsrett – Bedrift (Master Grade)',
        description: 'Prosesskontroll, dokumentgeneratorer og fristberegning for arbeidsgivere',
        status: 'active', premium: true, hasTeaser: true,
        features: ['Oppsigelsesprosess-veiviser', 'Arbeidskontraktgenerator', 'Fristberegning etter aml.', 'Advarselbrev og droftelsesmaler'],
      },
    ]
  },
  {
    id: 'oppvekst',
    title: 'Oppvekst & Utdanning',
    icon: GraduationCap,
    color: '#ec4899',
    modules: [
      {
        key: 'barnehage',
        name: 'Barnehage (Master Grade)',
        description: 'Juridisk stotte, saksbehandling og dokumentgeneratorer for barnehager',
        status: 'active', premium: true, hasTeaser: true,
        features: ['Opptaksprosess og venteliste', 'Tilsynsforberedelse', 'Bemanningsnorm-kalkulator', 'Vedtaksgenerator'],
      },
      {
        key: 'skole',
        name: 'Skole (Master Grade)',
        description: 'Juridisk stotte, saksbehandling og dokumentgeneratorer for skoler',
        status: 'active', premium: true, hasTeaser: true,
        features: ['Mobbesak-handtering (kap. 9A)', 'Spesialundervisning-vedtak', 'Tilsynssjekklister', 'Elevrettigheter og klager'],
      },
      {
        key: 'barnevern-master',
        name: 'Barnevern (Master Grade)',
        description: 'Komplett juridisk verktøykasse for barnevernstjenester',
        status: 'active', premium: true, hasTeaser: true,
        features: ['Undersokelsesprosess', 'Vedtaksgenerator bvl.', 'Akuttvedtak og tvangstiltak', 'Tilsyns- og dokumentasjonskrav'],
      },
    ]
  },
  {
    id: 'property',
    title: 'Eiendom & Utleie',
    icon: Home,
    color: '#5B8BA0',
    modules: [
      {
        key: 'husleierett-naering',
        name: 'Husleierett – Naering (Enterprise)',
        description: 'Naeringsleie, felleskostnader, MVA-matrise, KPI-regulering, vedlikehold og overtakelse',
        status: 'active', premium: true, hasTeaser: true,
        features: ['Naeringsleiekontrakt-analyse', 'Felleskostnad-kalkulator', 'KPI-reguleringsverktoy', 'Overtakelsesprotokoll'],
      },
    ]
  },
  {
    id: 'gdpr',
    title: 'GDPR & Personvern',
    icon: Shield,
    color: '#3b82f6',
    modules: [
      {
        key: 'gdpr',
        name: 'GDPR-modul',
        description: 'Databehandleravtaler, personvernerklaering, behandlingsprotokoller og avvikslogg',
        status: 'active', premium: true, hasTeaser: true,
        features: ['Databehandleravtale-generator', 'Behandlingsprotokoll', 'DPIA-verktoy', 'Avvikshandtering og logging'],
      },
    ]
  },
  {
    id: 'kontrakter',
    title: 'Kontraktsbibliotek',
    icon: FileSignature,
    color: '#B87333',
    modules: [
      {
        key: 'kontraktsbibliotek',
        name: 'Kontraktsbibliotek',
        description: 'Arbeidskontrakter, leverandoravtaler, tjenesteavtaler, leieavtaler og NDA-maler',
        status: 'active', premium: true, hasTeaser: true,
        features: ['Arbeidskontrakt-maler', 'Leverandoravtaler', 'NDA / Konfidensialitet', 'Tjeneste- og leieavtaler'],
      },
    ]
  },
  {
    id: 'compliance',
    title: 'Kommende bedriftsmoduler',
    icon: Shield,
    color: '#6366f1',
    modules: [
      {
        key: 'hms',
        name: 'HMS',
        description: 'Internkontroll, risikovurdering og HMS-dokumentasjon',
        status: 'coming', hasTeaser: false,
        comingSoonFeatures: ['Risikovurdering', 'Avvikshåndtering', 'Internkontroll-system', 'HMS-dokumentgenerator'],
      },
      {
        key: 'styrearbeid',
        name: 'Styrearbeid',
        description: 'Styreprotokoller, generalforsamling og vedtekter',
        status: 'coming', hasTeaser: false,
        comingSoonFeatures: ['Styreprotokoll-generator', 'Innkalling til generalforsamling', 'Vedtekter', 'Aksjeeierbok'],
      },
      {
        key: 'team',
        name: 'Team-samarbeid',
        description: 'Roller og tilgangsstyring for teamet',
        status: 'coming', hasTeaser: false,
        comingSoonFeatures: ['Rollehåndtering', 'Tilgangsnivåer', 'Aktivitetslogg', 'Samarbeidsverktøy'],
      },
      {
        key: 'api',
        name: 'API-tilgang',
        description: 'REST API for integrasjoner med andre systemer',
        status: 'coming', hasTeaser: false,
        comingSoonFeatures: ['REST API', 'Webhook-støtte', 'OAuth 2.0', 'Rate limiting'],
      },
    ]
  },
];

// ═══════════════════════════════════════════════════════════════
// GENERISK "KOMMER SNART" MODAL
// ═══════════════════════════════════════════════════════════════
function ComingSoonModal({ module, isOpen, onClose, onWaitlist }) {
  if (!isOpen || !module) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#1B2631] border border-[#2C3E50] rounded-2xl shadow-2xl p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#8899A8] hover:text-white">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#6366f1]/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#6366f1]" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">{module.name}</h3>
            <span className="text-xs text-[#6366f1] font-semibold">KOMMER SNART</span>
          </div>
        </div>
        <p className="text-sm text-[#8899A8] mb-4">{module.description}</p>
        {module.comingSoonFeatures && (
          <div className="space-y-2 mb-5">
            <p className="text-xs font-semibold text-[#8899A8] uppercase tracking-wider">Planlagt innhold:</p>
            {module.comingSoonFeatures.map((f, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-[#0F1419] rounded-lg">
                <CheckCircle className="w-3.5 h-3.5 text-[#6366f1]" />
                <span className="text-sm text-[#CCD6DD]">{f}</span>
              </div>
            ))}
          </div>
        )}
        <button
          onClick={() => { onWaitlist(module); onClose(); }}
          className="w-full py-2.5 bg-[#6366f1] text-white rounded-lg text-sm font-medium hover:bg-[#5558e6] transition-colors flex items-center justify-center gap-2"
          data-testid={`waitlist-${module.key}`}
        >
          <Send className="w-4 h-4" />
          Sett meg på venteliste
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODUL-KORT
// ═══════════════════════════════════════════════════════════════
function ModuleCard({ module, CategoryIcon, isLicensed, marketplaceMode, onInfoClick, onRequestAccess, requesting }) {
  const isActive = module.status === 'active' && isLicensed;
  const isComingSoon = module.status === 'coming';
  const canViewTeaser = module.hasTeaser && !isLicensed && !isComingSoon;

  const handleClick = () => {
    if (marketplaceMode) return; // Disable teaser navigation in marketplace
    onInfoClick(module);
  };

  return (
    <div
      className={`
        relative p-4 rounded-xl border text-left transition-all duration-300
        ${marketplaceMode && !isActive
          ? 'bg-gradient-to-br from-[#0F1419] to-[#162029] border-[#6366f1]/30 shadow-md shadow-[#6366f1]/5'
          : isActive
            ? 'bg-[#0F1419] border-[#2C3E50] hover:border-[#B87333] hover:shadow-lg cursor-pointer'
            : canViewTeaser
              ? 'bg-[#0F1419] border-[#2C3E50] hover:border-[#5B8BA0] cursor-pointer'
              : 'bg-[#0F1419]/50 border-dashed border-[#2C3E50]'
        }
      `}
      data-testid={`module-${module.key}`}
    >
      {/* Content area — clickable for info mode */}
      <div
        onClick={!marketplaceMode ? handleClick : undefined}
        className={!marketplaceMode && (isActive || canViewTeaser) ? 'cursor-pointer' : ''}
      >
        <div className="flex items-start justify-between mb-2">
          <h3 className={`font-medium pr-2 ${isActive ? 'text-white' : canViewTeaser ? 'text-[#CCD6DD]' : 'text-[#8899A8]'}`}>
            {module.name}
          </h3>
          {isActive ? (
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-[#22c55e]/20 text-[#22c55e] rounded flex-shrink-0">
              <CheckCircle className="w-3 h-3" /> AKTIV
            </span>
          ) : isComingSoon ? (
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-[#6366f1]/20 text-[#6366f1] rounded flex-shrink-0">
              <Sparkles className="w-3 h-3" /> KOMMER
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-[#B87333]/20 text-[#B87333] rounded flex-shrink-0">
              <Lock className="w-3 h-3" /> INAKTIV
            </span>
          )}
        </div>
        <p className={`text-sm mb-3 ${isActive ? 'text-[#8899A8]' : 'text-[#8899A8]/70'}`}>
          {module.description}
        </p>

        {/* Feature teaser — hidden in marketplace mode */}
        {!marketplaceMode && module.features && module.features.length > 0 && (
          <div className="mb-3 grid grid-cols-2 gap-1.5">
            {module.features.map((feat, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className={`w-1 h-1 rounded-full flex-shrink-0 ${isActive ? 'bg-[#B87333]' : 'bg-[#5B8BA0]'}`} />
                <span className={`text-xs ${isActive ? 'text-[#8899A8]' : 'text-[#8899A8]/60'}`}>{feat}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer — action area */}
      {marketplaceMode && !isActive ? (
        <button
          onClick={() => onRequestAccess(module)}
          disabled={requesting === module.key}
          className={`
            w-full mt-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all duration-200
            ${isComingSoon
              ? 'bg-[#6366f1]/20 text-[#6366f1] hover:bg-[#6366f1]/30'
              : 'bg-[#5B8BA0] text-white hover:bg-[#4a7a8f]'
            }
            disabled:opacity-50
          `}
          data-testid={`request-${module.key}`}
        >
          {requesting === module.key ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isComingSoon ? (
            <>
              <Send className="w-3.5 h-3.5" />
              Sett på venteliste
            </>
          ) : (
            <>
              <ArrowRight className="w-3.5 h-3.5" />
              Be om tilgang
            </>
          )}
        </button>
      ) : !marketplaceMode && (
        <div onClick={!marketplaceMode ? handleClick : undefined} className={!marketplaceMode && (isActive || canViewTeaser) ? 'cursor-pointer' : ''}>
          {isActive ? (
            <div className="flex items-center gap-1 text-[#B87333] text-sm">
              <span>Åpne modul</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          ) : canViewTeaser ? (
            <div className="flex items-center gap-1 text-[#5B8BA0] text-sm">
              <span>Se hva som kommer</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          ) : isComingSoon ? (
            <div className="space-y-2">
              {!marketplaceMode && module.comingSoonFeatures && (
                <div className="grid grid-cols-2 gap-1.5 mb-2">
                  {module.comingSoonFeatures.map((feat, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div className="w-1 h-1 rounded-full flex-shrink-0 bg-[#6366f1]/50" />
                      <span className="text-xs text-[#8899A8]/50">{feat}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-1 text-[#6366f1]/60 text-sm">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Under utvikling</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-[#8899A8]/50 text-sm">
              <Lock className="w-3.5 h-3.5" />
              <span>Krever aktivering</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// HOVEDKOMPONENT
// ═══════════════════════════════════════════════════════════════
export default function CompanyModules() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { isAdmin, isModuleLicensed } = useCompany();
  const [searchTerm, setSearchTerm] = useState('');
  const [marketplaceMode, setMarketplaceMode] = useState(false);
  const [requesting, setRequesting] = useState(null);
  const [comingSoonModule, setComingSoonModule] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Klikk på modul i info-modus → alltid vis teaser/info fra "Se alle moduler"
  const handleInfoClick = useCallback((module) => {
    if (module.hasTeaser) {
      navigate(`/company/modules/${module.key}`);
    } else if (module.status === 'coming') {
      setComingSoonModule(module);
    }
  }, [navigate]);

  // Be om tilgang / venteliste
  const handleRequestAccess = useCallback(async (module) => {
    if (module.status === 'coming') {
      setComingSoonModule(module);
      return;
    }
    setRequesting(module.key);
    try {
      const res = await axios.post(`${API}/module-request/activate`, {
        module_key: module.key,
        module_name: module.name,
      }, { headers });
      if (res.data.already_requested) {
        toast.info(res.data.message);
      } else {
        toast.success(res.data.message);
      }
    } catch {
      toast.error('Kunne ikke sende forespørsel. Prøv igjen.');
    }
    setRequesting(null);
  }, [headers]);

  // Venteliste for kommende moduler
  const handleWaitlist = useCallback(async (module) => {
    try {
      await axios.post(`${API}/module-request/activate`, {
        module_key: module.key,
        module_name: module.name,
      }, { headers });
      toast.success('Du er satt på venteliste!');
    } catch {
      toast.error('Kunne ikke sette på venteliste.');
    }
  }, [headers]);

  // Filtrer kategorier
  const filteredCategories = MODULE_CATEGORIES.map(cat => ({
    ...cat,
    modules: cat.modules.filter(m =>
      !searchTerm ||
      m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(cat => cat.modules.length > 0);

  const activeCount = MODULE_CATEGORIES.reduce(
    (acc, cat) => acc + cat.modules.filter(m => m.status === 'active' && isModuleLicensed(m.key)).length, 0
  );

  return (
    <div className="p-6 space-y-6" data-testid="company-modules">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Moduler</h1>
        <p className="text-[#8899A8] mt-1">
          {activeCount} aktive moduler tilgjengelig for din bedrift
        </p>
      </div>

      {/* Søk + Marketplace-knapp */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8899A8]" />
          <input
            type="text"
            placeholder="Søk etter modul..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#1B2631] border border-[#2C3E50] rounded-lg text-white placeholder-[#8899A8] focus:outline-none focus:border-[#B87333]"
            data-testid="module-search"
          />
        </div>

        {isAdmin && (
          <button
            onClick={() => setMarketplaceMode(prev => !prev)}
            className={`
              flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 flex-shrink-0
              ${marketplaceMode
                ? 'bg-[#5B8BA0] text-white shadow-lg shadow-[#5B8BA0]/20'
                : 'bg-[#1B2631] border border-[#5B8BA0]/50 text-[#5B8BA0] hover:bg-[#5B8BA0]/10'
              }
            `}
            data-testid="marketplace-toggle"
          >
            {marketplaceMode ? (
              <>
                <X className="w-4 h-4" />
                Lukk marketplace
              </>
            ) : (
              <>
                <ShoppingCart className="w-4 h-4" />
                Legg til moduler
              </>
            )}
          </button>
        )}
      </div>

      {/* Marketplace-banner */}
      {marketplaceMode && (
        <div className="p-4 bg-gradient-to-r from-[#5B8BA0]/15 to-[#6366f1]/10 border border-[#5B8BA0]/30 rounded-xl flex items-center gap-3 transition-all duration-300 animate-in fade-in"
          data-testid="marketplace-banner"
        >
          <ShoppingCart className="w-5 h-5 text-[#5B8BA0] flex-shrink-0" />
          <div>
            <p className="text-sm text-white font-medium">Marketplace-modus aktiv</p>
            <p className="text-xs text-[#8899A8]">
              Klikk «Be om tilgang» på moduler du ønsker å aktivere. En rådgiver kontakter deg.
            </p>
          </div>
        </div>
      )}

      {/* Kategorier */}
      <div className="space-y-6">
        {filteredCategories.map((category) => {
          const CategoryIcon = category.icon;
          return (
            <div
              key={category.id}
              className={`
                bg-[#1B2631] border rounded-xl p-5 transition-all duration-300
                ${marketplaceMode ? 'border-[#2C3E50]/80' : 'border-[#2C3E50]'}
              `}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-lg" style={{ backgroundColor: `${category.color}20` }}>
                  <CategoryIcon className="w-5 h-5" style={{ color: category.color }} />
                </div>
                <h2 className="text-lg font-semibold text-white">{category.title}</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {category.modules.map((module) => (
                  <ModuleCard
                    key={module.key}
                    module={module}
                    CategoryIcon={CategoryIcon}
                    isLicensed={isModuleLicensed(module.key)}
                    marketplaceMode={marketplaceMode}
                    onInfoClick={handleInfoClick}
                    onRequestAccess={handleRequestAccess}
                    requesting={requesting}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Generisk "Kommer snart" modal */}
      <ComingSoonModal
        module={comingSoonModule}
        isOpen={!!comingSoonModule}
        onClose={() => setComingSoonModule(null)}
        onWaitlist={handleWaitlist}
      />
    </div>
  );
}
